import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Phone, 
  PhoneIncoming, 
  PhoneOutgoing, 
  PhoneMissed,
  Clock,
  TrendingUp,
  RefreshCw,
  Filter,
  Calendar,
  Smartphone,
  MapPin,
  Radio
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line
} from "recharts";
import { toast } from "sonner";

import { API_URL } from '../utils/api';
const API = API_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

function StatCard({ title, value, subtitle, icon: Icon, color = "primary" }) {
  const colorClasses = {
    primary: "text-primary bg-primary/10",
    success: "text-emerald-500 bg-emerald-500/10",
    warning: "text-amber-500 bg-amber-500/10",
    error: "text-red-500 bg-red-500/10",
  };

  return (
    <Card className="bg-card border-border/50">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-heading font-bold mt-2">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Analytics() {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to format date as YYYY-MM-DD in local timezone
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Get date range for preset
  const getDateRange = (preset) => {
    const today = new Date();
    const todayStr = formatDate(today);
    
    switch (preset) {
      case 'today':
        return { from: todayStr, to: todayStr };
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = formatDate(yesterday);
        return { from: yesterdayStr, to: yesterdayStr };
      }
      case 'last7days': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { from: formatDate(weekAgo), to: todayStr };
      }
      case 'last30days': {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return { from: formatDate(monthAgo), to: todayStr };
      }
      case 'last60days': {
        const twoMonthsAgo = new Date(today);
        twoMonthsAgo.setDate(twoMonthsAgo.getDate() - 60);
        return { from: formatDate(twoMonthsAgo), to: todayStr };
      }
      case 'last90days': {
        const threeMonthsAgo = new Date(today);
        threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 90);
        return { from: formatDate(threeMonthsAgo), to: todayStr };
      }
      case 'thisMonth': {
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: formatDate(firstOfMonth), to: todayStr };
      }
      case 'lastMonth': {
        const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return { from: formatDate(firstOfLastMonth), to: formatDate(lastOfLastMonth) };
      }
      case 'all':
        return { from: '', to: '' };
      default:
        return { from: '', to: '' };
    }
  };

  // Default to last 30 days
  const defaultRange = getDateRange('last30days');
  
  const [filters, setFilters] = useState({
    date_from: defaultRange.from,
    date_to: defaultRange.to,
    datePreset: "last30days"
  });

  useEffect(() => {
    fetchAnalytics();
  }, [filters.date_from, filters.date_to]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      
      // Fetch analytics
      const analyticsRes = await axios.get(`${API}/analytics?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    const range = getDateRange(preset);
    setFilters({
      datePreset: preset,
      date_from: range.from,
      date_to: range.to
    });
  };

  // Handle manual date change (clears preset)
  const handleManualDateChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      datePreset: "custom"
    }));
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0m";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  // Format duration with total minutes in parentheses
  const formatDurationWithMinutes = (seconds) => {
    if (!seconds) return "0m (0m)";
    const totalMins = Math.floor(seconds / 60);
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) {
      return `${hrs}h ${mins}m (${totalMins}m)`;
    }
    return `${mins}m`;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  // Prepare chart data
  const callTypeData = analytics?.calls_by_type 
    ? Object.entries(analytics.calls_by_type).map(([name, value]) => ({ name, value }))
    : [];

  const hourlyData = analytics?.calls_by_hour || [];

  // Distribution data for pie chart
  const distributionData = [
    { name: 'Inbound', value: analytics?.inbound_calls || 0, color: '#10b981' },
    { name: 'Outbound', value: analytics?.outbound_calls || 0, color: '#3b82f6' },
    { name: 'Missed', value: analytics?.missed_calls || 0, color: '#ef4444' },
  ].filter(d => d.value > 0);

  const concurrencyData = analytics?.concurrency_by_hour || [];
  const concurrencyDistribution = analytics?.concurrency_distribution || [];

  return (
    <div className="p-6 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground">Call center performance insights</p>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            onClick={fetchAnalytics} 
            variant="outline"
            disabled={loading}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="bg-card border-border/50">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Date Range:</span>
            </div>
            <div className="w-48">
              <Select 
                value={filters.datePreset} 
                onValueChange={handleDatePresetChange}
              >
                <SelectTrigger className="bg-secondary/50" data-testid="analytics-date-preset">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last7days">Last 7 Days</SelectItem>
                  <SelectItem value="last30days">Last 30 Days</SelectItem>
                  <SelectItem value="last60days">Last 60 Days</SelectItem>
                  <SelectItem value="last90days">Last 90 Days</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="lastMonth">Last Month</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">From:</Label>
              <Input 
                type="date"
                value={filters.date_from}
                onChange={e => handleManualDateChange("date_from", e.target.value)}
                className="bg-secondary/50 w-40"
                data-testid="analytics-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">To:</Label>
              <Input 
                type="date"
                value={filters.date_to}
                onChange={e => handleManualDateChange("date_to", e.target.value)}
                className="bg-secondary/50 w-40"
                data-testid="analytics-date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Calls"
          value={analytics?.total_calls || 0}
          icon={Phone}
          color="primary"
        />
        <StatCard
          title="Inbound"
          value={analytics?.inbound_calls || 0}
          icon={PhoneIncoming}
          color="success"
        />
        <StatCard
          title="Outbound"
          value={analytics?.outbound_calls || 0}
          icon={PhoneOutgoing}
          color="warning"
        />
        <StatCard
          title="Missed"
          value={analytics?.missed_calls || 0}
          icon={PhoneMissed}
          color="error"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Talk Time"
          value={formatDuration(analytics?.total_duration_seconds || 0)}
          subtitle="Combined duration"
          icon={Clock}
          color="primary"
        />
        <StatCard
          title="Avg. Call Duration"
          value={formatDuration(analytics?.average_duration_seconds || 0)}
          subtitle="Per call average"
          icon={TrendingUp}
          color="success"
        />
        <StatCard
          title="Mobile Minutes"
          value={formatDurationWithMinutes(analytics?.mobile_seconds || 0)}
          subtitle="Calls to mobiles"
          icon={Smartphone}
          color="warning"
        />
        <StatCard
          title="Local + National"
          value={formatDurationWithMinutes(analytics?.local_national_seconds || 0)}
          subtitle="Landline minutes"
          icon={MapPin}
          color="primary"
        />
        <StatCard
          title="Peak Channels"
          value={analytics?.peak_concurrent_calls || 0}
          subtitle="Max simultaneous external calls"
          icon={Radio}
          color="warning"
          data-testid="peak-channels-stat"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Distribution */}
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Calls by Hour</CardTitle>
          </CardHeader>
          <CardContent>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={hourlyData}>
                  <defs>
                    <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#71717a"
                    tick={{ fill: '#71717a', fontSize: 12 }}
                    tickFormatter={(h) => `${h}:00`}
                  />
                  <YAxis 
                    stroke="#71717a"
                    tick={{ fill: '#71717a', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(h) => `${h}:00 - ${h}:59`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3b82f6" 
                    fillOpacity={1}
                    fill="url(#colorCalls)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No hourly data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Call Distribution */}
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Call Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {distributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={distributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No distribution data available
              </div>
            )}
            {/* Legend */}
            <div className="flex justify-center gap-6 mt-4">
              {distributionData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-muted-foreground">
                    {item.name}: {item.value}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Call Types */}
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Calls by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {callTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={callTypeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    type="number"
                    stroke="#71717a"
                    tick={{ fill: '#71717a', fontSize: 12 }}
                  />
                  <YAxis 
                    type="category"
                    dataKey="name"
                    stroke="#71717a"
                    tick={{ fill: '#71717a', fontSize: 12 }}
                    width={100}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#3b82f6"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No call type data available. Sync CDR records to see analytics.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Channel Concurrency by Hour */}
        <Card className="bg-card border-border/50" data-testid="concurrency-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Radio className="w-5 h-5 text-amber-500" />
              Peak Channels by Hour
            </CardTitle>
          </CardHeader>
          <CardContent>
            {concurrencyData.some(d => d.peak > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={concurrencyData}>
                  <defs>
                    <linearGradient id="colorChannels" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#71717a"
                    tick={{ fill: '#71717a', fontSize: 12 }}
                    tickFormatter={(h) => `${h}:00`}
                  />
                  <YAxis 
                    stroke="#71717a"
                    tick={{ fill: '#71717a', fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(h) => `${h}:00 - ${h}:59`}
                    formatter={(value) => [value, 'Peak concurrent']}
                  />
                  <Bar 
                    dataKey="peak" 
                    fill="url(#colorChannels)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No concurrency data available. Sync CDR records to see channel usage.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Concurrent Call Frequency Distribution */}
        <Card className="bg-card border-border/50" data-testid="concurrency-distribution-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Radio className="w-5 h-5 text-cyan-500" />
              Concurrent Call Frequency
            </CardTitle>
          </CardHeader>
          <CardContent>
            {concurrencyDistribution.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={concurrencyDistribution} layout="vertical">
                    <defs>
                      <linearGradient id="colorFreq" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      type="number"
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <YAxis 
                      type="category"
                      dataKey="concurrent_calls"
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      width={120}
                      tickFormatter={(v) => `${v}x Concurrent`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181b', 
                        border: '1px solid #27272a',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value} times`, 'Occurrences']}
                      labelFormatter={(v) => `${v}x Concurrent Calls`}
                    />
                    <Bar 
                      dataKey="occurrences" 
                      fill="url(#colorFreq)"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Number of times each concurrency level was reached when a new call started
                </p>
              </>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No concurrency distribution data available. Sync CDR records to see channel usage patterns.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
